import { uniq, map, union } from 'lodash';
import { SHIPPING_COST_THRESHOLD, SHIPPING_COST } from './appConstants';

export const getLineItemsProcessing = state => state.lineItemsProcessing;

export const getLineItemProcessingQuantity = (lineItem, state) => {
  const lineItemProcessing = state.lineItemsProcessing.find(
    item => item.sku === lineItem.sku
  );
  return lineItemProcessing && lineItemProcessing.quantity;
};

export const variantWithSku = (sku, state) => {
  const variant = state.variants.find(v => v.sku === sku);
  if (variant) {
    return variant;
  }
  reportMissingSku(sku, state.variants);
  return null;
};

export const productWithId = (id, state) => {
  return state.products.find(v => v.id === id);
};

export const savedItemWithSku = (sku, state) => {
  return orderLineItemWithSku(state.currentOrder, sku);
};

export const orderLineItemWithSku = (order, sku) => {
  return order.line_items.find(item => item.sku === sku);
};

export const hasPendingCollections = state => {
  return state.collectionsPending.length > 0;
};

export const getSidebarFocusState = state => {
  return state.sidebarIsFocused;
};

export const getCartOpenState = state => {
  return state.isOpen;
};

export const getShippingCost = ({ currentOrder }) => {
  const itemTotalCents = parseFloat(currentOrder.item_total) * 100;
  const hasFreeShipping =
    currentOrder.membership_order || itemTotalCents >= SHIPPING_COST_THRESHOLD;
  return hasFreeShipping ? 0 : SHIPPING_COST;
};

export const getPendingItemQuantityDiff = (pendingItem, state) => {
  const savedLineItem = savedItemWithSku(pendingItem.sku, state);
  const savedQuantity = savedLineItem ? savedLineItem.quantity : 0;
  return pendingItem.quantity - savedQuantity;
};

export const getPendingQuantity = state => {
  return state.lineItemsPending.reduce((total, item) => {
    return total + getPendingItemQuantityDiff(item, state);
  }, 0);
};

export const getPendingQuantitiesByLineItem = state => {
  const pendingQuantities = state.lineItemsPending.map(item => {
    return { sku: item.sku, quantity: getPendingItemQuantityDiff(item, state) };
  });

  return pendingQuantities.length ? pendingQuantities : 0;
};

export const getOrderLineItemQuantityDiff = (orderPrev, orderNew) => {
  const skusPrev = map(orderPrev.line_items, 'sku');
  const skusNew = map(orderNew.line_items, 'sku');
  const skus = uniq(union(skusPrev, skusNew));
  return skus.map(sku => {
    const lineItemDefault = { sku, quantity: 0 };
    const lineItemPrev =
      orderLineItemWithSku(orderPrev, sku) || lineItemDefault;
    const lineItemNew = orderLineItemWithSku(orderNew, sku) || lineItemDefault;
    const quantityDiff = lineItemNew.quantity - lineItemPrev.quantity;
    return { sku, quantityDiff, lineItemNew, lineItemPrev };
  });
};

export const getPendingPrice = state => {
  const { membershipDiscountRate, currentOrder, lineItemsPending } = state;
  const isMembershipOrder = currentOrder.membership_order;
  return lineItemsPending.reduce((total, item) => {
    const variant = variantWithSku(item.sku, state);
    const hasMembershipDiscount = isMembershipOrder && variant.is_subscribable;
    const discountRate = hasMembershipDiscount ? 1 - membershipDiscountRate : 1;
    const pricePerItem = (variant.price * discountRate).toFixed(2);
    return total + pricePerItem * getPendingItemQuantityDiff(item, state);
  }, 0);
};

export const getTotalSavings = state => {
  return getTotalSavingsForLineItems(state.currentOrder.line_items, state);
};

export const getTotalSavingsForLineItems = (lineItems, state) => {
  return lineItems.reduce((total, lineItem) => {
    return total + getLineItemSavings(lineItem, state);
  }, 0);
};

export const getLineItemsAtCartOpen = state => {
  const order = { ...state.currentOrder };
  const lineItems = order.line_items.slice();
  if (state.currentOrderAtCartOpen) {
    // we restore any 0 quantity line_items that existed
    // when the cart was first opened so that the user can
    // correct an accidental additional tap
    state.currentOrderAtCartOpen.line_items.forEach((lineItemOrig, index) => {
      if (!lineItems.find(item => item.sku === lineItemOrig.sku)) {
        lineItems.splice(index, 0, {
          ...lineItemOrig,
          quantity: 0,
        });
      }
    });
  }
  return lineItems;
};

export const getLineItemSavings = (lineItem, state) => {
  const promoTotal = Math.abs(parseFloat(lineItem.promo_total));
  return getLineItemAutoShipDiscount(lineItem, state) + promoTotal;
};

export const getLineItemAutoShipDiscount = lineItem => {
  return Math.abs(parseFloat(lineItem.membership_discount_total));
};

export const getLineItemsSubscribable = state => {
  return getLineItemsWithSubscribableValue(state, true);
};

export const getLineItems = state => {
  return state.currentOrder.line_items;
};

export const getLineItemsRegular = state => {
  return getLineItemsWithSubscribableValue(state, false);
};

export const getLineItemsSubscribableQuantity = state => {
  return getLineItemsTotalQuantity(getLineItemsSubscribable(state));
};

export const getLineItemsWithSubscribableValue = (state, value) => {
  return state.currentOrder.line_items.filter(item => {
    return variantWithSku(item.sku, state).is_subscribable === value;
  });
};

export const getLineItemsTotalQuantity = lineItems => {
  return lineItems.reduce((total, item) => {
    return total + item.quantity;
  }, 0);
};

export const getTotalPriceOfLineItems = (lineItems, state) => {
  return lineItems.reduce((total, lineItem) => {
    return (
      total +
      lineItem.price * lineItem.quantity -
      getLineItemSavings(lineItem, state)
    );
  }, 0);
};

export const reportMissingSku = (sku, variants) => {
  const error = new Error('Could not find variant for sku');
  error.sku = sku;
  error.variants = variants;

  bugsnagClient.notify(error);
};

// second argument allows us to easily mock the selector
export const getNumOfSubscribableItemsReqToCheckout = (
  state,
  subscribableQuantityFn = getLineItemsSubscribableQuantity
) => {
  const { minimumSubscriptionItems } = state;
  const isMembershipOrder = state.currentOrder.membership_order;
  const hasSubscriptionCommitment =
    state.currentOrder.has_subscription_commitment;
  const subscribableQuantity = subscribableQuantityFn(state);
  if (
    (isMembershipOrder && subscribableQuantity > 0) ||
    hasSubscriptionCommitment
  ) {
    return Math.max(minimumSubscriptionItems - subscribableQuantity, 0);
  }
  return 0;
};

export const getLineItemQuantitySaved = (state, props) => {
  const { currentOrder } = state;
  const lineItem = currentOrder.line_items.find(
    item => item.sku === props.variant.sku
  );
  return lineItem ? lineItem.quantity : 0;
};

export const getLineItemQuantityPending = (state, props) => {
  const { lineItemsPending } = state;
  const lineItemPending = lineItemsPending.find(
    item => item.sku === props.variant.sku
  );
  return lineItemPending
    ? lineItemPending.quantity
    : getLineItemQuantitySaved(state, props);
};



// WEBPACK FOOTER //
// ./app/javascript/apps/cart/selectors.js